/**
 * HOME
 * - ブランド、ゾーンの帯、副題、右上に設定
 * - 「続きから」カード（最新のシーン 1 件と次の工程）＋最近のシーン（残り 4 件）
 *   シーンが 0 件のときは「はじめに」（サンプルプリセットの案内、01 章・ゾーン早見表の入口）
 * - 振り返り待ちの帯（× でセッション中は隠す）
 * - 索引タイル（ライブラリ・分析・学ぶ・機材）
 * - 下端の主ボタン「新規シーンを撮影」：前回のプリセットがあればそのままビューファインダーへ。
 *   機材を変えるときは上の「機材を変える」から
 */

import {
  showScreen, getCurrentScreen, escapeHtml, icon, toast, formatRelative, blobUrl, releaseUrls, zoneCellsHtml,
} from './ui.js';
import { listShoots, countPendingReflection, getShoot } from './store.js';
import { setCurrentShoot } from './scene.js';
import { storedTimer } from './developer.js';
import { listPresets, getPreset, getActivePresetId, setActivePresetId } from './presets.js';
import { getFilmById } from './data/films.js';
import { ARTICLES } from './data/learn-content.js';
import { planLabel } from './zone-calc.js';

const BANNER_KEY = 'zn.banner.dismissed';

/** シーンの進み具合（0〜5）と次の工程 */
export function shootProgress(s) {
  const steps = [
    true,
    (s.spots?.length || 0) > 0,
    !!s.developmentPlan,
    !!s.development,
    !!s.printing,
  ];
  let done = 0;
  while (done < 5 && steps[done]) done++;
  const next = [
    '撮影',
    '測光ピンを置く',
    '露出を計算',
    '現像',
    'プリントを記録',
  ][done] || '振り返りを見る';
  return { done, steps, next: done >= 5 ? '振り返りを見る' : `次：${next}` };
}

/** HOME へ。ほかの画面から来たときは main.js の画面切替フックが描き直す */
export async function openHome(opts = {}) {
  const already = getCurrentScreen() === 'home';
  showScreen('home', opts);
  if (already) await renderHome();
}

export async function renderHome() {
  const el = document.getElementById('screen-home');
  releaseUrls('home');

  let shoots = [];
  let pending = 0;
  try {
    shoots = await listShoots({ limit: 5 });
    pending = await countPendingReflection();
  } catch (e) {
    console.warn('[home] load failed:', e);
  }
  let total = shoots.length;
  try { total = (await listShoots({ limit: 1000 })).length; } catch {}
  const presets = listPresets();
  const active = getPreset(getActivePresetId()) || presets[0];
  const presetLine = active
    ? `${active.camera || 'Camera'} · ${getFilmById(active.filmId)?.name || active.filmId} · EI ${active.ei}`
    : 'プリセットを選んで始める';
  let dismissed = false;
  try { dismissed = sessionStorage.getItem(BANNER_KEY) === '1'; } catch {}

  const latest = shoots[0];
  const rest = shoots.slice(1, 5);

  const strip = Array.from({ length: 11 }, (_, i) =>
    `<span class="zc-${i}" style="height:${(9 + i * 1.4).toFixed(1)}px;animation-delay:${120 + i * 45}ms"></span>`).join('');

  let mainBlock = '';
  if (latest) {
    const p = shootProgress(latest);
    const film = getFilmById(latest.filmId)?.name || latest.filmId || '';
    mainBlock = `
      <button class="cont" type="button" data-open="${escapeHtml(latest.id)}">
        <div class="cont__photo photo">
          ${latest.monoImageBlob ? `<img src="${blobUrl('home', latest.monoImageBlob)}" alt="" />` : ''}
          <span class="cont__tag">CONTINUE · 続きから</span>
          <div class="cont__meta">
            <div class="cont__title">${escapeHtml(latest.title || '無題のシーン')}</div>
            <div class="cont__sub">${escapeHtml([formatRelative(latest.updatedAt || latest.createdAt), latest.camera, film, latest.developmentPlan ? planLabel(latest.developmentPlan) : ''].filter(Boolean).join(' · '))}</div>
          </div>
        </div>
        <div class="cont__next">
          <div class="progress5">${p.steps.map((_, i) => `<span class="${i < p.done ? 'is-on' : ''}"></span>`).join('')}</div>
          <span style="flex:1;font:500 13px/1.3 var(--font-jp-sans)">${escapeHtml(p.next)}</span>
          ${icon('arrow', 18)}
        </div>
      </button>
      <div class="recent">
          <div class="recent__head"><span class="eyebrow">recent · 最近のシーン</span><button type="button" data-go="library" style="font:500 12px/1 var(--font-jp-sans);padding:4px 0">すべて</button></div>
          ${rest.length ? `<div class="recent__list">
            ${rest.map((s, i) => `
              <button class="recent__item" type="button" data-open="${escapeHtml(s.id)}" style="animation:zn-up .45s ${380 + i * 60}ms both">
                <div class="recent__thumb thumb">
                  ${s.monoImageBlob ? `<img src="${blobUrl('home', s.monoImageBlob)}" alt="" />` : ''}
                  <span class="recent__plan">${escapeHtml(s.developmentPlan ? planLabel(s.developmentPlan) : '—')}</span>
                </div>
                <div class="recent__t">${escapeHtml(s.title || '無題')}</div>
              </button>`).join('')}
          </div>` : ''}
        </div>
    `;
  } else {
    const first = ARTICLES[0];
    const names = presets.slice(0, 3).map((p) => p.name).join('、');
    mainBlock = `
      <div class="getting">
        <div class="eyebrow">getting started</div>
        <div class="getting__t">${presets.length ? `機材プリセットを ${presets.length} 件用意しています。` : '機材プリセットを登録しましょう。'}</div>
        <p class="getting__d">${presets.length ? `${escapeHtml(names)}。` : ''}下の「新規シーンを撮影」から始められます。</p>
        <div class="getting__btns">
          ${first ? `<button class="btn-line" type="button" data-article="${escapeHtml(first.id)}">${escapeHtml(first.chapter)} ${escapeHtml(first.title)} を読む</button>` : ''}
          <button class="btn-rule" type="button" data-go="zone-chart" style="padding:0 16px;height:48px">ゾーン早見表</button>
        </div>
      </div>`;
  }

  const tiles = [
    ['library', 'grid', 'ライブラリ', String(total)],
    ['analytics', 'bars', '分析', ''],
    ['learn', 'learn', '学ぶ', String(ARTICLES.length)],
    ['gear', 'gear', '機材', String(presets.length)],
  ];

  el.innerHTML = `
    <div class="safe-top"></div>
    <div class="bar" style="padding:0 10px 0 8px">
      <div class="home-brand"><span class="home-brand__en">zone notes</span><span class="home-brand__jp">ゾーンノート</span></div>
      <button class="bar__btn" type="button" data-go="settings" aria-label="設定">${icon('settings', 22)}</button>
    </div>
    <div class="body" style="display:flex;flex-direction:column">
      <div style="padding:6px var(--gutter) 0;flex:none">
        <div class="home-strip">${strip}</div>
        <p class="home-lead">中判フィルムの露出・現像・プリントを現場から振り返りまで一貫して記録する。</p>
      </div>
      ${mainBlock}
      ${pending > 0 && !dismissed ? `
        <div class="wait" id="home-wait">
          <span class="wait__n">${pending}</span>
          <span class="wait__t">シーンが振り返り待ち</span>
          <button class="wait__link" type="button" data-go="library-wait">ライブラリ</button>
          <button class="wait__x" type="button" data-wait-close aria-label="閉じる">${icon('close', 14)}</button>
        </div>` : ''}
      ${timerStrip()}
      <div class="tiles">
        ${tiles.map(([go, ic, t, n], i) => `
          <button class="tile" type="button" data-go="${go}" style="animation-delay:${400 + i * 60}ms">
            <div class="tile__top">${icon(ic, 18)}<span class="tile__n">${escapeHtml(n)}</span></div>
            <span class="tile__t">${t}</span>
          </button>`).join('')}
      </div>
      <div class="home-cta">
        ${active ? `<div style="display:flex;justify-content:space-between;align-items:center;padding:0 4px 8px"><span class="sub" style="font:400 12px/1.4 var(--font-jp-sans)">前回の機材ですぐ撮影します</span><button type="button" data-go="select-gear" style="font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">機材を変える</button></div>` : ''}
        <button class="cta" type="button" data-go="new-shoot">
          <span class="cta__dot"><span></span></span>
          <div style="flex:1;min-width:0"><div class="cta__t">新規シーンを撮影</div><div class="cta__s">${escapeHtml(presetLine)}</div></div>
          ${icon('arrow')}
        </button>
      </div>
    </div>
  `;

  el.querySelectorAll('[data-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.go)));
  el.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => {
    import('./reflection.js').then((m) => m.openShootById(b.dataset.open));
  }));
  el.querySelector('[data-article]')?.addEventListener('click', (e) => {
    import('./learn.js').then((m) => m.openLearnArticle(e.currentTarget.dataset.article));
  });
  el.querySelector('[data-open-timer]')?.addEventListener('click', async (e) => {
    const shoot = await getShoot(e.currentTarget.dataset.openTimer);
    if (!shoot) {
      // シーンが削除されていたらタイマーの記録も片付ける
      try { localStorage.removeItem('zn.dev.timer'); } catch {}
      toast('シーンが見つかりません');
      renderHome();
      return;
    }
    setCurrentShoot(shoot);
    import('./developer.js').then((m) => m.openDevelopment());
  });
  el.querySelector('[data-wait-close]')?.addEventListener('click', () => {
    try { sessionStorage.setItem(BANNER_KEY, '1'); } catch {}
    document.getElementById('home-wait')?.remove();
  });
}

/** 14. 現像タイマーが動いている（一時停止中・完了待ちを含む）ときの帯 */
function timerStrip() {
  const t = storedTimer();
  if (!t) return '';
  const fmt = (x) => `${Math.floor(x / 60)}:${String(Math.floor(x % 60)).padStart(2, '0')}`;
  const state = t.done ? '完了 · 記録待ち' : t.running ? '計測中' : '一時停止中';
  return `
    <button class="wait" type="button" data-open-timer="${escapeHtml(t.shootId)}" style="width:calc(100% - 32px);text-align:left;box-shadow:inset 0 0 0 1.5px var(--color-ink)">
      ${icon('moon', 18)}
      <span class="wait__t">現像タイマー · ${state}</span>
      <span class="num" style="font:400 15px/1 var(--font-en);padding-right:4px">${fmt(t.elapsedSec)}${t.target ? ` / ${fmt(t.target)}` : ''}</span>
      <span class="wait__link">開く</span>
    </button>`;
}

function go(target) {
  switch (target) {
    case 'new-shoot': {
      // 1. 前回の機材があれば、選択画面を通らずにそのままビューファインダーへ
      const p = getPreset(getActivePresetId()) || listPresets()[0];
      if (p) {
        setActivePresetId(p.id);
        import('./camera.js').then((m) => m.openViewfinder(p));
      }
      else import('./gear.js').then((m) => m.openSelectGear());
      break;
    }
    case 'select-gear': import('./gear.js').then((m) => m.openSelectGear()); break;
    case 'library': import('./library.js').then((m) => m.openLibrary()); break;
    case 'library-wait': import('./library.js').then((m) => m.openLibrary({ filter: 'pending' })); break;
    case 'analytics': import('./analytics.js').then((m) => m.openAnalytics()); break;
    case 'learn': import('./learn.js').then((m) => m.openLearnIndex()); break;
    case 'gear': import('./gear.js').then((m) => m.openGear()); break;
    case 'settings': import('./settings.js').then((m) => m.openSettings()); break;
    case 'zone-chart': import('./zone-chart.js').then((m) => m.openZoneChart()); break;
    default: break;
  }
}
